import { defineMessages } from "react-intl";

const ARROW_PLACEMENT = Object.freeze({
  logo: "topLeft",
  sidebar: "leftTop",
  widget: "topRight",
  waffle: "leftTop",
});

const STEP_NAMES = Object.freeze({
  logo: "logo",
  sidebar: "sidebar",
  widget: "widget",
  waffle: "waffle",
  orgSignerWaffle: "orgSignerWaffle",
});

const COMMON_MESSAGES = defineMessages({
  next: {
    id: "b5dc6dc8-92f9-41b5-a746-46c41fac75d9",
    defaultMessage: "Next",
  },
  nextLabel: {
    id: "0a8cf7fe-278f-4841-98ef-389a44517fd9",
    defaultMessage: "Next step in walkthrough",
  },
  finish: {
    id: "375164ca-9a21-4285-8539-58f650e21a88",
    defaultMessage: "Finish",
  },
  finishLabel: {
    id: "3fc850a2-5844-4239-9c1c-13115c176620",
    defaultMessage: "Finish walkthrough",
  },
  dismiss: {
    id: "babb6011-cf9a-45c6-83a0-e0bf7baf999e",
    defaultMessage: "Dismiss",
  },
  dismissLabel: {
    id: "7b8eb851-205a-4029-8441-b1940cbabbe5",
    defaultMessage: "Dismiss walkthrough",
  },
  dontShowAgain: {
    id: "148d0af8-320a-4dee-a0d6-e1a1611c37a3",
    defaultMessage: "Don't show this message again",
  },
});

const ORGANIZATION_MESSAGES = defineMessages({
  heading: {
    id: "18453977-6400-4ddc-b84f-b54deea40dbb",
    defaultMessage: "Get to know our navigation",
  },
  sidebar: {
    id: "9ac9b323-bf9b-4196-8430-1f97845e1d02",
    defaultMessage:
      "See your organization's name and navigate between organization features in the sidebar. Expand and collapse with the arrow.",
  },
  widget: {
    id: "4b132a4f-8360-40f3-b603-cfe2e9d0a3ad",
    defaultMessage:
      "Navigate to your personal Notarize space from your initials. This includes your user profile and personal documents.",
  },
  widgetNotary: {
    id: "b1c81990-e497-412d-bcdf-035ad70a8de8",
    defaultMessage:
      "Navigate to your personal Notarize space from your initials. This includes your user profile, notary profile, and personal documents.",
  },
});

const ORGANIZATION_LOGO_MESSAGES = defineMessages({
  title: {
    id: "8a3b2bb6-078a-425f-829d-57a35b2951da",
    defaultMessage:
      "This is your global navigation bar. “Notarize for Title” indicates that you are in our title agent app.",
  },
  lender: {
    id: "86b53a1b-71a1-44fe-a00a-af90be0d1b77",
    defaultMessage:
      "This is your global navigation bar. “Notarize for Lenders” indicates that you are in our lender app.",
  },
  business: {
    id: "d5cef7be-3b2b-43f6-a65b-4213084bda5b",
    defaultMessage:
      "This is your global navigation bar. “Notarize for Business” indicates that you are in our business app.",
  },
  notary: {
    id: "a7027fcd-be7e-46f1-8133-298ea4e00ccf",
    defaultMessage:
      "This is your global navigation bar. “Notarize for Notaries” indicates that you are in our notary app.",
  },
});

const ORGANIZATION_WAFFLE_MESSAGES = defineMessages({
  title: {
    id: "1acf3bbc-71fd-4412-9e49-fea2cb97104a",
    defaultMessage:
      "Get back to Notarize for Title from your personal Notarize space by opening up the dot menu in the top left corner.",
  },
  lender: {
    id: "fd89fd75-fb85-4475-afb0-033df85bb802",
    defaultMessage:
      "Get back to Notarize for Lenders from your personal Notarize space by opening up the dot menu in the top left corner.",
  },
  business: {
    id: "2771b421-000f-48bd-b5af-45949e782de9",
    defaultMessage:
      "Get back to Notarize for Business from your personal Notarize space by opening up the dot menu in the top left corner.",
  },
  notary: {
    id: "38040c7b-05f5-48b0-907f-e0c75d82d700",
    defaultMessage:
      "Get back to Notarize for Notaries from your personal Notarize space by opening up the dot menu in the top left corner.",
  },
});

export const NOTARIZE_ORGANIZATION_STEPS = [
  {
    stepName: STEP_NAMES.logo,
    heading: ORGANIZATION_MESSAGES.heading,
    description: {
      title: ORGANIZATION_LOGO_MESSAGES.title,
      lender: ORGANIZATION_LOGO_MESSAGES.lender,
      business: ORGANIZATION_LOGO_MESSAGES.business,
      notary: ORGANIZATION_LOGO_MESSAGES.notary,
    },
    buttonText: COMMON_MESSAGES.next,
    buttonLabel: COMMON_MESSAGES.nextLabel,
    arrowPlacement: ARROW_PLACEMENT.logo,
  },
  {
    stepName: STEP_NAMES.sidebar,
    heading: ORGANIZATION_MESSAGES.heading,
    description: {
      title: ORGANIZATION_MESSAGES.sidebar,
      lender: ORGANIZATION_MESSAGES.sidebar,
      business: ORGANIZATION_MESSAGES.sidebar,
      notary: ORGANIZATION_MESSAGES.sidebar,
    },
    buttonText: COMMON_MESSAGES.next,
    buttonLabel: COMMON_MESSAGES.nextLabel,
    arrowPlacement: ARROW_PLACEMENT.sidebar,
  },
  {
    stepName: STEP_NAMES.widget,
    heading: ORGANIZATION_MESSAGES.heading,
    description: {
      title: ORGANIZATION_MESSAGES.widget,
      lender: ORGANIZATION_MESSAGES.widget,
      business: ORGANIZATION_MESSAGES.widget,
      notary: ORGANIZATION_MESSAGES.widgetNotary,
    },
    buttonText: COMMON_MESSAGES.next,
    buttonLabel: COMMON_MESSAGES.nextLabel,
    arrowPlacement: ARROW_PLACEMENT.widget,
  },
  {
    stepName: STEP_NAMES.waffle,
    heading: ORGANIZATION_MESSAGES.heading,
    description: {
      title: ORGANIZATION_WAFFLE_MESSAGES.title,
      lender: ORGANIZATION_WAFFLE_MESSAGES.lender,
      business: ORGANIZATION_WAFFLE_MESSAGES.business,
      notary: ORGANIZATION_WAFFLE_MESSAGES.notary,
    },
    buttonText: COMMON_MESSAGES.finish,
    buttonLabel: COMMON_MESSAGES.finishLabel,
    arrowPlacement: ARROW_PLACEMENT.waffle,
  },
];

const ORGANIZATION_SIGNER_MESSAGES = defineMessages({
  heading: {
    id: "b92c9675-616a-45cd-b2f8-4941bfc19929",
    defaultMessage: "Looking for your business space?",
  },
  title: {
    id: "53a75e7c-d758-4b2e-8b7f-981d7f8e4584",
    defaultMessage:
      "You are in your personal Notarize space. Get to Notarize for Title by clicking this menu.",
  },
  lender: {
    id: "ba7a7a24-304d-4f6d-b125-df3c46ef69ce",
    defaultMessage:
      "You are in your personal Notarize space. Get to Notarize for Lenders by clicking this menu.",
  },
  business: {
    id: "9cafc6af-3b01-4956-b9a6-84a4e1bd3dff",
    defaultMessage:
      "You are in your personal Notarize space. Get to Notarize for Business by clicking this menu.",
  },
  notary: {
    id: "7a9859f8-0cce-4be3-b82f-176a89ed3886",
    defaultMessage:
      "You are in your personal Notarize space. Get to Notarize for Notaries by clicking this menu.",
  },
});
// Org user in the signer app looks different than the rest so special stepName and mismatched arrow placement name
export const NOTARIZE_ORGANIZATION_SIGNER_STEPS = [
  {
    stepName: STEP_NAMES.orgSignerWaffle,
    heading: ORGANIZATION_SIGNER_MESSAGES.heading,
    description: {
      title: ORGANIZATION_SIGNER_MESSAGES.title,
      lender: ORGANIZATION_SIGNER_MESSAGES.lender,
      business: ORGANIZATION_SIGNER_MESSAGES.business,
      notary: ORGANIZATION_SIGNER_MESSAGES.notary,
    },
    buttonText: COMMON_MESSAGES.dismiss,
    buttonLabel: COMMON_MESSAGES.dismissLabel,
    arrowPlacement: ARROW_PLACEMENT.logo,
  },
];
